
import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';

interface AlertsResponse {
  lupsSendFailed: {
    id: string;
    advanceRequestId: string;
    installmentDetailId: string;
    sendAttempts: number;
    lastError: string | null;
    nextRetryAt: string | null;
  }[];
  abandonedPending: {
    id: string;
    advanceRequestId: string;
    installmentDetailId: string;
    abandonedAt: string;
  }[];
  accountChangesHigh: {
    id: string;
    merchantId: string;
    codigoComercio: string;
    montoRiesgo: string;
    createdAt: string;
  }[];
}

const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const formatCLP = (n: number | string) => Number(n).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

export default function AdminAlertasPage() {
  const [alerts, setAlerts] = useState<AlertsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminHttpClient.get<AlertsResponse>(getAdminEndpointUrl('alerts', 'list'));
      setAlerts(data);
    } catch {
      setAlerts(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleResolveAccountChange = async (id: string) => {
    const resolution = window.prompt('Resolución del cambio de cuenta:');
    if (!resolution?.trim()) return;
    try {
      await adminHttpClient.post(getAdminEndpointUrl('alerts', 'resolveAccountChange', { id }), { resolution: resolution.trim() });
      await load();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    }
  };

  return (
    <AdminLayout title="Alertas centralizadas" subtitle="Lo que requiere atención manual del equipo">
      <div className="space-y-6 max-w-6xl">
        {loading && <p className="text-sm text-muted-foreground">Cargando…</p>}

        {!loading && alerts && (
          <>
            <AlertSection
              title="Fallos de envío a Lups"
              description="Payment requests con estado SEND_FAILED > 12h sin resolver. El scheduler LupsRetryScheduler sigue reintentando en background."
              emptyLabel="No hay fallos pendientes 🎉"
              items={alerts.lupsSendFailed}
              renderItem={(f) => (
                <div className="flex flex-wrap items-start justify-between gap-3 py-3 border-t border-border">
                  <div className="min-w-0">
                    <p className="font-mono text-xs">{f.advanceRequestId} · cuota {f.installmentDetailId.slice(0, 8)}…</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {f.sendAttempts} intento{f.sendAttempts === 1 ? '' : 's'}
                      {f.nextRetryAt && <> · siguiente {formatDate(f.nextRetryAt)}</>}
                    </p>
                    {f.lastError && <p className="mt-0.5 text-xs text-red-600 truncate max-w-[60ch]" title={f.lastError}>{f.lastError}</p>}
                  </div>
                  <a href={`/admin/solicitudes/detalle?id=${f.advanceRequestId}`} className="text-xs text-flux-primary hover:underline shrink-0">Ver adelanto →</a>
                </div>
              )}
            />

            <AlertSection
              title="Cuotas abandonadas sin resolución"
              description="Cuotas que FLUX abandonó al vencer su fecha de abandono y todavía no tienen nota de seguimiento (PAC manual, gestión de cobranza, aceptar pérdida)."
              emptyLabel="No hay cuotas abandonadas pendientes"
              items={alerts.abandonedPending}
              renderItem={(t) => (
                <div className="flex flex-wrap items-start justify-between gap-3 py-3 border-t border-border">
                  <div className="min-w-0">
                    <p className="font-mono text-xs">{t.advanceRequestId} · cuota {t.installmentDetailId.slice(0, 8)}…</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Abandonada el {formatDate(t.abandonedAt)}</p>
                  </div>
                  <a href="/admin/abandoned-installments" className="text-xs text-flux-primary hover:underline shrink-0">Registrar nota →</a>
                </div>
              )}
            />

            <AlertSection
              title="Cambios de cuenta HIGH sin liberar"
              description="Cambios de cuenta detectados por el monitoreo con riesgo > 0 que aún no se liberaron manualmente. Revisar y registrar resolución."
              emptyLabel="Sin cambios de cuenta HIGH abiertos"
              items={alerts.accountChangesHigh}
              renderItem={(a) => (
                <div className="flex flex-wrap items-start justify-between gap-3 py-3 border-t border-border">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Comercio <span className="font-mono">{a.merchantId}</span> · código <span className="font-mono">{a.codigoComercio}</span></p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Detectado {formatDate(a.createdAt)} · riesgo {formatCLP(a.montoRiesgo)}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <a href={`/admin/empresas/detalle?id=${a.merchantId}`} className="text-xs text-flux-primary hover:underline">Ver comercio →</a>
                    <a href={`/admin/alertas/account-changes?id=${a.id}`} className="text-xs text-flux-primary hover:underline">Respaldo Lups →</a>
                    <button type="button" onClick={() => handleResolveAccountChange(a.id)} className="text-xs rounded-md border border-border px-2 py-0.5 hover:bg-accent">Liberar</button>
                  </div>
                </div>
              )}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function AlertSection<T>({ title, description, items, emptyLabel, renderItem }: { title: string; description: string; items: T[]; emptyLabel: string; renderItem: (item: T) => React.ReactNode }) {
  return (
    <section className="rounded-xl border bg-card p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        <span className="shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-medium">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">{emptyLabel}</p>
      ) : (
        <div className="mt-2">
          {items.map((item, idx) => (
            <div key={idx}>{renderItem(item)}</div>
          ))}
        </div>
      )}
    </section>
  );
}
